import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  FolderOpen,
  HardDrive,
  Mail,
  MessageSquareText,
  Settings2,
  Sparkles,
} from 'lucide-react';
import { Card, TextInput } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import AiModelsSettings from './AiModelsSettings';
import AiUsageDashboard from './AiUsageDashboard';
import EmailAiPromptsSettings from './EmailAiPromptsSettings';
import ApplicationSettingsModule from './ApplicationSettingsModule';
import FilesStoragePage from '../settings/FilesStoragePage';
import SystemEmailTemplatesModule from './SystemEmailTemplatesModule';
import EmailNotificationSettingsModule from './EmailNotificationSettingsModule';
import GoogleSyncSettingsModule from './GoogleSyncSettingsModule';
import type { HubGroupDefinition, HubModuleDefinition } from './hubConfig';

export const moduleConfig = {
  module: {
    title: 'Settings',
    description: 'Manage system settings, AI, and application configuration.',
  },
};

type ConfigGroupId = 'system' | 'ai_automation';

type ConfigModuleIcon = React.ComponentType<{ className?: string }>;

interface ConfigModuleDefinition extends HubModuleDefinition {
  id: string;
  shortDescription: string;
  icon: ConfigModuleIcon;
}

interface ConfigGroupDefinition extends Omit<HubGroupDefinition, 'id' | 'modules'> {
  id: ConfigGroupId;
  modules: ConfigModuleDefinition[];
}

const CONFIG_GROUPS: ConfigGroupDefinition[] = [
  {
    id: 'system',
    title: 'System',
    description: 'Application-wide settings for administrators.',
    modules: [
      {
        id: 'application_settings',
        title: 'Application settings',
        shortDescription: 'Features, email & layout',
        description:
          'Feature flags, API timeouts, dashboard layout, and company profile. SMTP lives under Email notifications.',
        route: '/settings',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        icon: Settings2,
        component: ApplicationSettingsModule,
      },
      {
        id: 'email_notifications',
        title: 'Email notifications',
        shortDescription: 'SMTP & test send',
        description: 'Enable transactional email, configure default SMTP, and send a test message.',
        route: '/admin/settings/email-notifications',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        icon: Mail,
        component: EmailNotificationSettingsModule,
      },
      {
        id: 'google_sync',
        title: 'Google Sync',
        shortDescription: 'OAuth credentials',
        description: 'Google OAuth credentials for Gmail sign-in and Google Calendar sync.',
        route: '/admin/settings/google-sync',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        icon: Sparkles,
        component: GoogleSyncSettingsModule,
      },
      {
        id: 'system_email_templates',
        title: 'System email templates',
        shortDescription: 'Invites & reset mail',
        description: 'Customize subject and HTML for user invitations and password reset emails.',
        route: '/admin/settings/system-email-templates',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        icon: MessageSquareText,
        component: SystemEmailTemplatesModule,
      },
      {
        id: 'files_storage',
        title: 'File storage',
        shortDescription: 'Attachments & orphans',
        description: 'Audit file attachments on disk, review orphaned records, and manage soft-deleted files.',
        route: '/admin/settings/files-storage',
        permissionModuleKey: 'deleted_files',
        hubPassesEmbedded: true,
        icon: HardDrive,
        component: FilesStoragePage,
      },
    ],
  },
  {
    id: 'ai_automation',
    title: 'AI & Automation',
    description: 'Models, prompts, and usage monitoring.',
    modules: [
      {
        id: 'ai_models',
        title: 'AI Models',
        shortDescription: 'Providers & defaults',
        description: 'Save model/provider configuration used by AI features.',
        route: '/configuration-settings?group=ai_automation&module=ai_models',
        permissionModuleKey: 'configuration_settings',
        hubPassesEmbedded: true,
        icon: Bot,
        component: AiModelsSettings,
      },
      {
        id: 'ai_usage',
        title: 'AI usage',
        shortDescription: 'Requests & tokens',
        description: 'Monitor requests and token usage per provider (OpenAI, Gemini, Ollama).',
        route: '/configuration-settings?group=ai_automation&module=ai_usage',
        permissionModuleKey: 'configuration_settings',
        hubPassesEmbedded: true,
        icon: BarChart3,
        component: AiUsageDashboard,
      },
      {
        id: 'email_ai_prompts',
        title: 'Email AI prompts',
        shortDescription: 'Assistant prompts',
        description: 'Create and edit prompts for the email AI assistant drawer (placeholders, compose vs read).',
        route: '/ai-prompts',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        icon: FolderOpen,
        component: EmailAiPromptsSettings,
      },
    ],
  },
];

interface ConfigSettingsNavProps {
  groups: ConfigGroupDefinition[];
  activeModuleId: string | undefined;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (groupId: ConfigGroupId, moduleId: string) => void;
}

const ConfigSettingsNav: React.FC<ConfigSettingsNavProps> = ({
  groups,
  activeModuleId,
  searchTerm,
  onSearchChange,
  onSelect,
}) => {
  const { colors } = useTheme();
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredGroups = React.useMemo(() => {
    if (!normalizedSearch) return groups;
    return groups
      .map((group) => ({
        ...group,
        modules: group.modules.filter((module) => {
          const haystack = `${module.title} ${module.shortDescription} ${module.description}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        }),
      }))
      .filter((group) => group.modules.length > 0);
  }, [groups, normalizedSearch]);

  const allModules = React.useMemo(
    () => filteredGroups.flatMap((group) => group.modules.map((module) => ({ group, module }))),
    [filteredGroups],
  );

  const renderButton = (
    groupId: ConfigGroupId,
    item: ConfigModuleDefinition,
    compact: boolean,
  ) => {
    const Icon = item.icon;
    const active = activeModuleId === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onSelect(groupId, item.id)}
        className={cn(
          'text-left transition-all duration-200',
          compact
            ? cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium',
                active
                  ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
                  : cn(colors.border, colors.textSecondary, colors.surface, colors.interactiveHover),
              )
            : cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-3',
                active
                  ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                  : cn(colors.textSecondary, colors.interactiveHover),
              ),
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-4 w-4' : 'h-9 w-9',
            !compact && (active ? 'bg-white/15' : cn(colors.surface, 'border', colors.border)),
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        {compact ? (
          <span>{item.title}</span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className={cn('block text-sm font-medium', active ? colors.primaryText : colors.text)}>
              {item.title}
            </span>
            <span className={cn('mt-0.5 block truncate text-xs', active ? 'opacity-90' : colors.textMuted)}>
              {item.shortDescription}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div className={cn('border-b p-3 lg:hidden', colors.border)}>
        <TextInput
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search settings…"
          className="mb-3"
        />
        <div className="flex gap-2 overflow-x-auto pb-1">
          {allModules.map(({ group, module }) => renderButton(group.id, module, true))}
        </div>
      </div>

      <nav
        className={cn(
          'hidden lg:flex lg:w-64 lg:shrink-0 lg:flex-col lg:gap-1 lg:border-r lg:p-3',
          colors.border,
        )}
      >
        <div className="mb-2 px-1">
          <TextInput
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search settings…"
          />
        </div>

        {filteredGroups.length === 0 ? (
          <p className={cn('px-3 py-4 text-xs', colors.textMuted)}>No settings match your search.</p>
        ) : (
          filteredGroups.map((group, index) => (
            <div key={group.id} className="flex flex-col gap-1">
              <p
                className={cn(
                  'px-3 pb-1 text-[10px] font-bold uppercase tracking-wider',
                  index > 0 && 'pt-3',
                  colors.textMuted,
                )}
              >
                {group.title}
              </p>
              {group.modules.map((module) => renderButton(group.id, module, false))}
            </div>
          ))
        )}
      </nav>
    </>
  );
};

const ConfigurationSettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();

  const searchTerm = searchParams.get('search') || '';
  const selectedGroupIdFromUrl = searchParams.get('group');
  const selectedModuleIdFromUrl = searchParams.get('module');

  const canView = React.useCallback(
    (moduleKey: string) => {
      if (user?.role === 'ADMIN' || !permissionsLoaded) {
        return true;
      }
      return can(moduleKey, 'view');
    },
    [can, permissionsLoaded, user?.role],
  );

  const visibleGroups = React.useMemo(() => {
    return CONFIG_GROUPS.map((group) => ({
      ...group,
      modules: group.modules.filter((module) => {
        if (module.adminOnly && user?.role !== 'ADMIN') {
          return false;
        }
        return canView(module.permissionModuleKey);
      }),
    })).filter((group) => group.modules.length > 0);
  }, [canView, user?.role]);

  const allVisibleModules = React.useMemo(
    () => visibleGroups.flatMap((group) => group.modules),
    [visibleGroups],
  );

  const selectedModule = React.useMemo(() => {
    if (selectedModuleIdFromUrl) {
      const fromUrl = allVisibleModules.find((module) => module.id === selectedModuleIdFromUrl);
      if (fromUrl) return fromUrl;
    }
    const group =
      visibleGroups.find((g) => g.id === selectedGroupIdFromUrl) ?? visibleGroups[0];
    return group?.modules[0];
  }, [allVisibleModules, selectedModuleIdFromUrl, selectedGroupIdFromUrl, visibleGroups]);

  const selectedGroup = React.useMemo(() => {
    if (!selectedModule) {
      return visibleGroups.find((group) => group.id === selectedGroupIdFromUrl) ?? visibleGroups[0];
    }
    return (
      visibleGroups.find((group) => group.modules.some((module) => module.id === selectedModule.id)) ??
      visibleGroups[0]
    );
  }, [selectedModule, selectedGroupIdFromUrl, visibleGroups]);

  const updateParams = React.useCallback(
    (next: { group?: string; module?: string; search?: string }) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next.group !== undefined) {
            if (next.group) {
              params.set('group', next.group);
            } else {
              params.delete('group');
            }
          }
          if (next.module !== undefined) {
            if (next.module) {
              params.set('module', next.module);
            } else {
              params.delete('module');
            }
          }
          if (next.search !== undefined) {
            if (next.search.trim()) {
              params.set('search', next.search);
            } else {
              params.delete('search');
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    if (!selectedGroup || !selectedModule) {
      return;
    }

    const needsGroup = selectedGroupIdFromUrl !== selectedGroup.id;
    const needsModule = selectedModuleIdFromUrl !== selectedModule.id;
    if (needsGroup || needsModule) {
      updateParams({
        group: selectedGroup.id,
        module: selectedModule.id,
      });
    }
  }, [
    selectedGroup,
    selectedModule,
    selectedGroupIdFromUrl,
    selectedModuleIdFromUrl,
    updateParams,
  ]);

  if (visibleGroups.length === 0) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full space-y-6 p-4 sm:p-6">
          <Card className="p-8 text-center">
            <h1 className={cn('text-2xl font-semibold', colors.text)}>Settings</h1>
            <p className={cn('mt-3 text-sm', colors.textMuted)}>
              No configuration modules are available for your current permissions.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const SelectedListComponent = selectedModule?.component;

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full space-y-6 p-4 sm:p-6">
        <div className={cn('rounded-2xl border px-5 py-5 sm:px-6', colors.border, colors.surface)}>
          <div className="flex items-start gap-4">
            <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', colors.info)}>
              <Settings2 className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Settings</h1>
              <p className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>
                System configuration for administrators: application options, email delivery, Google
                sync, file storage, and AI.
              </p>
            </div>
          </div>
        </div>

        <Card className="overflow-hidden">
          <div className="flex min-h-0 flex-col lg:min-h-[36rem] lg:flex-row">
            <ConfigSettingsNav
              groups={visibleGroups}
              activeModuleId={selectedModule?.id}
              searchTerm={searchTerm}
              onSearchChange={(value) => updateParams({ search: value })}
              onSelect={(groupId, moduleId) => updateParams({ group: groupId, module: moduleId })}
            />

            <div className="min-w-0 flex-1 p-4 sm:p-6">
              {selectedModule && SelectedListComponent ? (
                <div key={selectedModule.id}>
                  {React.createElement(
                    SelectedListComponent as React.ComponentType<{ embedded?: boolean }>,
                    { embedded: true },
                  )}
                </div>
              ) : (
                <p className={cn('text-sm', colors.textMuted)}>
                  Select a settings area from the navigation.
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ConfigurationSettingsPage;
